import { ReaderPresentation } from '../../shared/reader-presentation.component';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiClientService } from '../../core/api-client.service';
import { I18nService } from '../../core/i18n.service';
import { LocalePreferenceService } from '../../core/locale-preference.service';
import { PageDataService } from '../../core/page-data.service';
import { SiteConfigService } from '../../core/site-config.service';
import { AuthService } from '../../core/auth.service';

type MediaSizes = Record<string, unknown> | unknown[] | null | undefined;

export interface FortuneListingCampaign {
  id: number;
  title: string;
  campaignType: string;
  discountType: string | null;
  discountValue: number | null;
  buyQuantity: number | null;
  payQuantity: number | null;
  sellerID: number | null;
}

export interface FortuneFeatureValue {
  id: number;
  title: string;
  count: number;
}

export interface FortuneFeatureFacet {
  id: number;
  key?: string;
  title: string;
  values: FortuneFeatureValue[];
}

export interface FortuneListing {
  id: number;
  versionID?: number | null;
  sellerID: number;
  sellerSlug?: string | null;
  sellerShopName?: string | null;
  title: string;
  slug: string;
  description?: string | null;
  categoryID?: number | null;
  categorySlug?: string | null;
  categoryTitle: string;
  categoryPath?: Array<{ title: string; slug?: string | null }>;
  imageUrl: string | null;
  imageSizes: MediaSizes;
  images: Array<{ url: string | null; sizes: MediaSizes }>;
  price: number | null;
  currency: string;
  seoPrice?: number | null;
  seoCurrency?: string | null;
  creditUnitValue?: number;
  sellerName: string;
  sellerImage: string | null;
  sellerImageSizes: MediaSizes;
  rating: number;
  reviewCount: number;
  orderCount: number;
  listingType?: string | null;
  freeShipping?: boolean;
  liveStatus: string | null;
  presenceStatus?: string | null;
  presenceLastSeenAt?: string | null;
  pendingFortunes?: number;
  sellerScore?: number | null;
  listingScore?: number | null;
  statusLabel: string | null;
  statusCode: string | null;
  rankTitle?: string | null;
  rankReminderName?: string | null;
  isNewReader: boolean;
  online: boolean;
  highlightText: string | null;
  campaign?: FortuneListingCampaign | null;
  campaignPrice?: number | null;
}

export interface FortunePaymentGateway {
  id: number;
  method: string;
  fastPayment: boolean;
  listingTypeID?: number | null;
}

export interface FortuneListingPage {
  items: FortuneListing[];
  page: number;
  total: number;
  hasMore: boolean;
  features: FortuneFeatureFacet[];
}

export interface FortuneCategory {
  id: number;
  versionID?: number | null;
  name: string;
  title: string;
  slug: string;
  description?: string | null;
  metaTitle?: string | null;
  metaDescription?: string | null;
  parentID: number | null;
  imageUrl: string | null;
  imageSizes: MediaSizes;
}

export interface FortuneFaq {
  id: number;
  title: string;
  content: string;
  slug?: string | null;
  updatedAt?: string | null;
}

export interface FortuneSeller {
  presentation?: ReaderPresentation|null;
  id: number;
  sellerName: string;
  shopName: string;
  profilePicture: string | null;
  profilePictureSizes: MediaSizes;
  rating: number;
  reviewCount: number;
  orderCount: number;
  liveStatus: string | null;
  statusLabel: string | null;
  statusCode: string | null;
  isNewReader: boolean;
  rankTitle: string | null;
  rankReminderName?: string | null;
  highlightText: string | null;
  online?: boolean;
  presenceStatus?: string | null;
  presenceLastSeenAt?: string | null;
  pendingFortunes?: number;
  sellerScore?: number | null;
  listingCount?: number;
  minCreditPrice?: number | null;
  offerCategoryIDs?: number[];
  followerCount?: number;
}

export interface FortuneSellerListResult {
  items: FortuneSeller[];
  page: number;
  total: number;
  hasMore: boolean;
}

export interface FortuneOptionSub {
  id: number;
  title: string;
  name?: string | null;
  active?: boolean;
  info: string | null;
  price: number | null;
}

export interface FortuneOption {
  id: number;
  name: string;
  title?: string | null;
  processType: string;
  required: boolean;
  subOptions: FortuneOptionSub[];
}

export interface FortuneCustomization {
  id: number;
  formKey: string;
  type: string;
  title: string;
  description: string | null;
  required: boolean;
  minLength: number | null;
  maxLength: number | null;
  minFiles?: number | null;
  maxFiles?: number | null;
  options: Array<{ title: string; value: string }>;
}

export interface FortuneListingDetail extends FortuneListing {
  listingType?: string;
  durationMinutes?: number;
  description: string;
  options: FortuneOption[];
  customization: FortuneCustomization[];
  laws: Record<string, unknown>;
  policies: Record<string, unknown>;
  shipping: unknown;
  preparationDays?: number | null;
  reviews: Array<Record<string, unknown>>;
  sellerReviews: Array<Record<string, unknown>>;
  shippingDetails: Record<string, unknown> | null;
  faqs: unknown;
  shippingUnavailable?: boolean;
  paymentGateways: FortunePaymentGateway[];
}

@Injectable({ providedIn: 'root' })
export class FortuneService {
  private readonly api = inject(ApiClientService);
  private readonly i18n = inject(I18nService);
  private readonly localePreference = inject(LocalePreferenceService);
  private readonly pageData = inject(PageDataService);
  private readonly site = inject(SiteConfigService);
  private readonly auth = inject(AuthService);

  async listings(params: { sellerID?: number | null; category?: string; categoryID?:number; search?: string; minPrice?:number; maxPrice?:number; freeShipping?:boolean; sort?:string; limit?: number; page?: number; optionFilter?: Record<string, number[]>; campaignOnly?: boolean } = {}): Promise<FortuneListing[]> {
    const result = await this.listingPage(params);
    return result.items;
  }

  seededListingSnapshot(params: { sellerID?: number | null; category?: string; categoryID?:number; search?: string; minPrice?:number; maxPrice?:number; freeShipping?:boolean; sort?:string; limit?: number; page?: number; optionFilter?: Record<string, number[]>; campaignOnly?: boolean } = {}): FortuneListingPage | null {
    return this.seededListingPage(params, params.limit ?? 12);
  }

  async categoryFaqs(categoryID: number): Promise<FortuneFaq[]> {
    await this.site.ready();
    const seededResponse = this.pageData.get<{ data?: { categoryID?: number; items?: FortuneFaq[] } }>('fortuneFaqs');
    if (seededResponse?.data && Number(seededResponse.data.categoryID ?? 0) === Number(categoryID)) {
      return seededResponse.data.items ?? [];
    }
    const response = await firstValueFrom(this.api.post<{ data?: { items?: FortuneFaq[] } }>('faqs', {
      categoryID,
      ...this.localePayload()
    }));
    return response.data?.items ?? [];
  }

  async listingPage(params: { sellerID?: number | null; category?: string; categoryID?:number; search?: string; minPrice?:number; maxPrice?:number; freeShipping?:boolean; sort?:string; limit?: number; page?: number; optionFilter?: Record<string, number[]>; campaignOnly?: boolean } = {}): Promise<FortuneListingPage> {
    const page = Math.max(0, params.page ?? 0);
    const limit = params.limit ?? 12;
    // SSR already owns an authoritative listing snapshot in pageData. Consume
    // it before browser-only locale preference initialization so the first
    // HTML contains crawlable product links instead of a loading shell.
    if (typeof window === 'undefined') {
      const serverSeed = this.seededListingPage(params, limit);
      if (serverSeed) return serverSeed;
    }
    await this.site.ready();
    await this.i18n.ready();
    // Do not reuse SSR's default-currency seed before the persisted account
    // preference has been loaded. This is especially important on storefront
    // home rails, which call listings immediately after hydration.
    await this.localePreference.load();
    const seededPage = page === 0 ? this.seededListingPage(params, limit) : null;
    if (seededPage) {
      return seededPage;
    }

    const useDirectSlugFilter = this.site.experience('fortune');
    const listingsApiPath = this.site.config().brandKey === 'bdsmbeds' ? '/next/listings' : 'listings';
    const response = await firstValueFrom(this.api.post<unknown>(listingsApiPath, {
      page,
      limit,
      direct: true,
      count: true,
      ...this.localePayload(),
      sellerID: params.sellerID || undefined,
      // The legacy PHP listing viewer expects `category` to be a numeric
      // category ID, while Fal Deryası's direct resolver uses the localized
      // slug. Sending both made Hobyle pass a slug into the numeric filter and
      // caused navigation results to be unscoped or empty.
      category: useDirectSlugFilter ? (params.category || undefined) : undefined,
      categoryID: params.categoryID || undefined,
      search: params.search || undefined,
      min: params.minPrice || undefined,
      max: params.maxPrice || undefined,
      shipping: params.freeShipping ? 'free' : undefined,
      orderBy: params.sort || undefined,
      optionFilter: params.optionFilter && Object.keys(params.optionFilter).length ? params.optionFilter : undefined,
      'campaign-listings': params.campaignOnly ? true : undefined,
      getFeatures: true
    }));

    const result = this.extractListingsPage(response, page, limit);
    return {
      ...result,
      items: result.items.map((item) => this.normalizeListing(item))
    };
  }

  async sellers(params: { search?: string; limit?: number; page?: number } = {}): Promise<FortuneSeller[]> {
    const result = await this.sellerList(params);
    return result.items;
  }

  seededSellerList(): FortuneSellerListResult | null {
    const response = this.pageData.get<unknown>('sellers');
    if (!response) return null;
    const result = this.extractSellersResult(response);
    if (!result.items.length) return null;
    return {
      ...result,
      items: result.items.map((item) => this.normalizeSeller(item))
    };
  }

  async sellerList(params: { search?: string; limit?: number; page?: number; sort?: string; presence?: string; categoryID?: number; sellerIDs?: number[] } = {}): Promise<FortuneSellerListResult> {
    await this.site.ready();
    const page = Math.max(1, params.page ?? 1);
    const seededResponse = page === 1 && !params.search && !params.presence && !params.categoryID && params.sellerIDs === undefined && (!params.sort || params.sort === 'score') ? this.pageData.get<unknown>('sellers') : null;
    const response = seededResponse ?? await firstValueFrom(this.api.post<unknown>('sellers', {
      page,
      limit: params.limit ?? 10,
      direct: true,
      ...this.localePayload(),
      search: params.search || undefined,
      sort: params.sort || 'score',
      presence: params.presence || undefined,
      categoryID: params.categoryID || undefined,
      sellerIDs: params.sellerIDs,
      offersOnly: this.site.usesSalesModel('categoryOffer')
    }));

    const result = this.extractSellersResult(response);
    return {
      ...result,
      items: result.items.map((item) => this.normalizeSeller(item))
    };
  }

  async categories(): Promise<FortuneCategory[]> {
    await this.site.ready();
    await this.i18n.ready();
    const seededResponse = this.pageData.get<{ data?: { items?: FortuneCategory[] } }>('fortuneCategories');
    if (seededResponse?.data?.items) {
      return seededResponse.data.items.map((item) => this.normalizeCategory(item));
    }

    const payload = this.localePayload();
    const response = await firstValueFrom(this.api.post<{ data: { items: FortuneCategory[] } }>('categories', {
      ...payload,
      ...(this.site.config().capabilities.commerce ? { platform: 'products' } : {})
    }));
    return (response.data?.items ?? []).map((item) => this.normalizeCategory(item));
  }

  async listingDetail(slug: string, allowSeed = false, offerToken = ''): Promise<FortuneListingDetail | null> {
    await this.site.ready();
    await this.i18n.ready();
    // Load the persisted browser/account preference before deciding whether
    // SSR seed data can be reused. Otherwise a reload can briefly treat a
    // EUR/USD visitor as the site's default TRY visitor.
    await this.localePreference.load();
    const seededResponse = this.pageData.get<unknown>('listingData');
    // SSR page data is generated with the server's locale. Once the visitor
    // selected a currency in the browser, reusing that seed would show the
    // converted currency label with the old amount. Fetch the detail again.
    const seededData = seededResponse === null ? null : this.extractListingDetail(seededResponse, slug);
    const useSeed = allowSeed && !this.localePreference.currency().trim() && String(seededData?.['slug'] ?? '') === slug;
    const response = (useSeed ? seededResponse : null) ?? await firstValueFrom(this.api.post<unknown>('listingDetail', {
      slug,
      direct: true,
      ...(offerToken ? { offerToken } : {}),
      ...this.localePayload()
    }));
    const data = this.extractListingDetail(response, slug);
    const detail = data ? this.normalizeListingDetail(data) : null;
    if (detail) {
      this.applySeededSellerSnapshot(detail);
      if (typeof window !== 'undefined' && this.site.experience('fortune')) {
        await this.refreshListingSellerSnapshot(detail);
      }
    }
    if (detail?.categoryID) {
      const categories = await this.categories();
      const byID = new Map(categories.map((category) => [category.id, category]));
      const path: FortuneCategory[] = [];
      let current = byID.get(detail.categoryID);
      while (current && !path.some((entry) => entry.id === current?.id)) {
        path.unshift(current);
        current = current.parentID ? byID.get(current.parentID) : undefined;
      }
      if (path.length) {
        detail.categoryPath = path.map((category) => ({ title: category.title, slug: category.slug }));
        detail.categoryTitle = path.at(-1)?.title ?? detail.categoryTitle;
        detail.categorySlug = path.at(-1)?.slug ?? detail.categorySlug;
      }
    }
    if (detail?.categoryID) {
      const categories = await this.categories();
      const byID = new Map(categories.map((category) => [category.id, category]));
      const path: FortuneCategory[] = [];
      let current = byID.get(detail.categoryID);
      while (current && !path.some((entry) => entry.id === current?.id)) {
        path.unshift(current);
        current = current.parentID ? byID.get(current.parentID) : undefined;
      }
      if (path.length) {
        detail.categoryPath = path.map((category) => ({ title: category.title, slug: category.slug }));
        detail.categoryTitle = path.at(-1)?.title ?? detail.categoryTitle;
        detail.categorySlug = path.at(-1)?.slug ?? detail.categorySlug;
      }
    }
    if (detail) this.rememberListing(detail);
    return detail;
  }

  /** Returns the SSR page-data listing without starting a client request. */
  seededListingDetail(): FortuneListingDetail | null {
    const response = this.pageData.get<unknown>('listingData');
    const data = response === null ? null : this.extractListingDetail(response);
    const detail = data ? this.normalizeListingDetail(data) : null;
    if (detail) this.applySeededSellerSnapshot(detail);
    return detail;
  }

  recentListings(limit = 8): FortuneListing[] {
    if (typeof window === 'undefined') return [];
    try {
      const key = `aymudo_recent_listings_${this.site.config().brandKey}`;
      const stored: unknown = JSON.parse(window.localStorage.getItem(key) || '[]');
      if (!Array.isArray(stored)) return [];
      return stored.filter((item): item is FortuneListing => this.isRecord(item) && Number(item['id'] ?? 0) > 0).slice(0, limit);
    } catch {
      return [];
    }
  }

  private rememberListing(detail: FortuneListingDetail): void {
    if (typeof window === 'undefined') return;
    try {
      const key = `aymudo_recent_listings_${this.site.config().brandKey}`;
      const current = this.recentListings(24).filter((item) => item.id !== detail.id);
      const item: FortuneListing = {
        id: detail.id,
        versionID: detail.versionID,
        sellerID: detail.sellerID,
        sellerSlug: detail.sellerSlug,
        sellerShopName: detail.sellerShopName,
        title: detail.title,
        slug: detail.slug,
        categoryID: detail.categoryID,
        categorySlug: detail.categorySlug,
        categoryTitle: detail.categoryTitle,
        imageUrl: detail.imageUrl,
        imageSizes: detail.imageSizes,
        images: detail.images,
        price: detail.price,
        currency: detail.currency,
        sellerName: detail.sellerName,
        sellerImage: detail.sellerImage,
        sellerImageSizes: detail.sellerImageSizes,
        rating: detail.rating,
        reviewCount: detail.reviewCount,
        orderCount: detail.orderCount,
        liveStatus: detail.liveStatus,
        statusLabel: detail.statusLabel,
        statusCode: detail.statusCode,
        isNewReader: detail.isNewReader,
        online: detail.online,
        highlightText: detail.highlightText
      };
      window.localStorage.setItem(key, JSON.stringify([item, ...current].slice(0, 24)));
    } catch {
      // Browsing history is optional and must never block a listing page.
    }
  }

  async relatedListings(detail: FortuneListingDetail): Promise<{ sameSeller: FortuneListing[]; sameCategory: FortuneListing[] }> {
    const [sellerListings, categoryListings] = await Promise.all([
      detail.sellerID > 0 ? this.listings({ sellerID: detail.sellerID, limit: 8 }) : Promise.resolve([]),
      this.listings({ limit: 24 })
    ]);
    return {
      sameSeller: sellerListings.filter((item) => item.id !== detail.id).slice(0, 4),
      sameCategory: categoryListings.filter((item) => item.id !== detail.id && Number(item.categoryID ?? 0) === Number(detail.categoryID ?? 0)).slice(0, 4)
    };
  }

  private extractListings(response: unknown): Record<string, unknown>[] {
    const root = Array.isArray(response) ? response : [response];
    for (const item of root as Record<string, unknown>[]) {
      const data = item?.['data'];
      if (this.isRecord(data) && Array.isArray(data['items'])) {
        return data['items'] as Record<string, unknown>[];
      }
      if (this.isRecord(data) && Array.isArray(data['listings'])) {
        return data['listings'] as Record<string, unknown>[];
      }
      if (Array.isArray(item?.['items'])) {
        return item['items'] as Record<string, unknown>[];
      }
      const direct = item?.['listings'];
      if (this.isRecord(direct) && Array.isArray(direct['listings'])) {
        return direct['listings'] as Record<string, unknown>[];
      }

      const apiData = item?.['apiData'];
      if (this.isRecord(apiData) && this.isRecord(apiData['listings']) && Array.isArray(apiData['listings']['listings'])) {
        return apiData['listings']['listings'] as Record<string, unknown>[];
      }
    }

    return [];
  }

  private extractListingsPage(response: unknown, requestedPage: number, requestedLimit: number): { items: Record<string, unknown>[]; page: number; total: number; hasMore: boolean; features: FortuneFeatureFacet[] } {
    const root = Array.isArray(response) ? response : [response];
    for (const item of root as Record<string, unknown>[]) {
      const data = this.isRecord(item?.['data']) ? item['data'] : item;
      const collection = this.isRecord(data?.['listings']) ? data['listings'] : data;
      if (!this.isRecord(collection) || !Array.isArray(collection['items'] ?? collection['listings'])) {
        continue;
      }
      const items = (collection['items'] ?? collection['listings']) as Record<string, unknown>[];
      const page = Number(collection['page'] ?? requestedPage);
      const total = Number(collection['total'] ?? collection['totalCount'] ?? items.length);
      const hasMore = Boolean(collection['hasMore'])
        || total > (Math.max(1, page) * requestedLimit)
        || (items.length >= requestedLimit && items.length > 0);
      // API versions have used both `features` and `featureFacets`; accept the
      // current and legacy envelopes so the filter UI does not disappear when
      // the listing payload is otherwise valid.
      const rawFeatures = collection['features']
        ?? collection['featureFacets']
        ?? collection['categoryFeatures']
        ?? (this.isRecord(data) ? data['features'] ?? data['featureFacets'] ?? data['categoryFeatures'] : undefined);
      return { items, page, total: Math.max(total, items.length), hasMore, features: this.normalizeFeatureFacets(rawFeatures) };
    }
    const items = this.extractListings(response);
    return { items, page: requestedPage, total: items.length, hasMore: items.length >= requestedLimit, features: [] };
  }

  private seededListingPage(params: { sellerID?: number | null; category?: string; categoryID?:number; search?: string; minPrice?:number; maxPrice?:number; freeShipping?:boolean; sort?:string; limit?: number; optionFilter?: Record<string, number[]>; campaignOnly?: boolean }, limit: number): FortuneListingPage | null {
    // SSR listing data is generated with the server's default locale. It is
    // safe for the first server render, but on the browser it can contain a
    // stale TRY amount while the selected account locale is another currency.
    // Always refresh listing prices in the browser so the amount and currency
    // come from the same localized API response. Product detail already uses
    // this rule; listings must do the same.
    const isBdsmbedsHomepage = this.site.config().brandKey === 'bdsmbeds'
      && !params.category && !params.categoryID && !params.sellerID && !params.search
      && !params.minPrice && !params.maxPrice && !params.freeShipping
      && !params.optionFilter && !params.campaignOnly;
    if (typeof window !== 'undefined' && !isBdsmbedsHomepage) {
      return null;
    }
    // Category SSR receives an authoritative scoped snapshot. Reuse it on the
    // server so crawlers and no-JS clients see the same products as hydration.
    if (params.sellerID || params.search || params.minPrice || params.maxPrice || params.freeShipping || params.optionFilter || params.campaignOnly || (!params.category && !params.categoryID && !isBdsmbedsHomepage && (params.sort || this.localePreference.currency().trim()))) {
      return null;
    }
    const seededResponse = this.pageData.get<unknown>('fortuneListings');
    const result = this.extractListingsPage(seededResponse, 1, limit);
    if (!result.items.length) {
      return null;
    }
    if (params.categoryID && result.items.some((item) => Number(item['categoryID'] ?? 0) !== Number(params.categoryID))) {
      return null;
    }
    if (params.category) {
      const expected = params.category.trim().toLocaleLowerCase();
      if (result.items.some((item) => {
        const slug = String(item['categorySlug'] ?? '').trim().toLocaleLowerCase();
        return slug && slug !== expected;
      })) return null;
    }

    return {
      ...result,
      items: result.items.map((item) => this.normalizeListing(item))
    };
  }

  private normalizeCategory(item: FortuneCategory): FortuneCategory {
    const raw = item as FortuneCategory & Record<string, unknown>;
    const categoryLang = this.isRecord(raw['categoryLang']) ? raw['categoryLang'] : {};
    const categoryVersion = this.isRecord(raw['categoryVersion']) ? raw['categoryVersion'] : {};
    const parentValue = raw['parentID'] ?? categoryVersion['parentID'];
    return {
      ...item,
      versionID: raw['versionID'] !== undefined && raw['versionID'] !== null
        ? Number(raw['versionID'])
        : (categoryVersion['id'] !== undefined && categoryVersion['id'] !== null ? Number(categoryVersion['id']) : null),
      title: String(categoryLang['title'] ?? item.title ?? raw['name'] ?? ''),
      description: item.description
        ?? (raw['shortDescription'] as string | null | undefined)
        ?? (raw['desciption'] as string | null | undefined)
        ?? (categoryLang['description'] as string | null | undefined)
        ?? null,
      metaTitle: (raw['metaTitle'] ?? categoryLang['metaTitle'] ?? null) as string | null,
      metaDescription: (raw['metaDescription'] ?? categoryLang['metaDescription'] ?? null) as string | null,
      parentID: parentValue === null || parentValue === undefined || parentValue === ''
        ? null
        : Number(parentValue),
      imageUrl: this.absoluteMediaUrl(item.imageUrl ?? ''),
      imageSizes: this.parseSizes(item.imageSizes)
    };
  }

  private extractSellers(response: unknown): Record<string, unknown>[] {
    return this.extractSellersResult(response).items;
  }

  private extractSellersResult(response: unknown): { items: Record<string, unknown>[]; page: number; total: number; hasMore: boolean } {
    const root = Array.isArray(response) ? response : [response];
    for (const item of root as Record<string, unknown>[]) {
      const directData = this.isRecord(item?.['data']) ? item['data'] : null;
      if (directData && Array.isArray(directData['list'])) {
        const pagination = this.isRecord(directData['pagination']) ? directData['pagination'] : {};
        return {
          items: directData['list'] as Record<string, unknown>[],
          page: Number(pagination['page'] ?? 1),
          total: Number(pagination['total'] ?? directData['list'].length),
          hasMore: !!pagination['hasMore']
        };
      }

      const sellers = this.isRecord(item?.['apiData']) ? item['apiData']['sellers'] : item?.['sellers'];
      if (this.isRecord(sellers) && this.isRecord(sellers['data']) && Array.isArray(sellers['data']['list'])) {
        const pagination = this.isRecord(sellers['data']['pagination']) ? sellers['data']['pagination'] : {};
        return {
          items: sellers['data']['list'] as Record<string, unknown>[],
          page: Number(pagination['page'] ?? 1),
          total: Number(pagination['total'] ?? sellers['data']['list'].length),
          hasMore: !!pagination['hasMore']
        };
      }
    }

    return { items: [], page: 1, total: 0, hasMore: false };
  }

  private extractListingDetail(response: unknown, expectedSlug = ''): Record<string, unknown> | null {
    const normalizeSlug = (value: unknown): string => {
      const raw = String(value ?? '').trim().toLowerCase();
      try {
        return decodeURIComponent(raw).replace(/^\/+|\/+$/g, '');
      } catch {
        return raw.replace(/^\/+|\/+$/g, '');
      }
    };
    const wantedSlug = normalizeSlug(expectedSlug);
    const accept = (candidate: Record<string, unknown> | null): Record<string, unknown> | null => {
      if (!candidate) return null;
      if (!wantedSlug) return candidate;
      const candidateSlug = normalizeSlug(
        candidate['slug'] ?? candidate['listingSlug'] ?? candidate['productSlug']
      );
      return candidateSlug === wantedSlug ? candidate : null;
    };
    const root = Array.isArray(response) ? response : [response];
    for (const item of root as Record<string, unknown>[]) {
      const directData = this.isRecord(item?.['data']) ? item['data'] : null;
      if (this.isRecord(directData?.['listingData'])) {
        const listingData = directData['listingData'] as Record<string, unknown>;
        const normalized = this.isRecord(listingData['listingData']) ? listingData['listingData'] as Record<string, unknown> : listingData;
        const accepted = accept({
          ...normalized,
          laws: directData['laws'] ?? normalized['laws'],
          policies: directData['policies'] ?? normalized['policies'],
          shipping: directData['shipping'] ?? directData['shippingOptions'] ?? normalized['shipping'] ?? normalized['shippingDetails'],
          faqs: directData['faqs'] ?? directData['faq'] ?? normalized['faqs'] ?? normalized['faq'],
          options: directData['optionGroups'] ?? normalized['options'],
          customization: directData['customizationFields'] ?? normalized['customization']
        });
        if (accepted) return accepted;
      }
      if (directData && (directData['id'] || directData['slug'] || directData['title'])) {
        const accepted = accept(directData);
        if (accepted) return accepted;
      }
      if (this.isRecord(item?.['listingData'])) {
        const listingData = item['listingData'] as Record<string, unknown>;
        const accepted = accept(this.isRecord(listingData['listingData']) ? listingData['listingData'] as Record<string, unknown> : listingData);
        if (accepted) return accepted;
      }
      const direct = this.isRecord(item?.['apiData']) ? item['apiData']['listingData'] : null;
      if (this.isRecord(direct)) {
        const accepted = accept(this.isRecord(direct['listingData']) ? direct['listingData'] as Record<string, unknown> : direct);
        if (accepted) return accepted;
      }
      const fallbackDirect = this.isRecord(item?.['apiData']) ? item['apiData']['directListingDetails'] : null;
      if (this.isRecord(fallbackDirect)) {
        const accepted = accept(fallbackDirect);
        if (accepted) return accepted;
      }
      if (item?.['id'] || item?.['slug'] || item?.['title']) {
        const accepted = accept(item);
        if (accepted) return accepted;
      }
    }

    // The detail endpoint and the SSR page-data endpoint do not always use
    // the same envelope. In particular, client navigation can return
    // data.apiData.listingData.listingData (or one more listingData layer).
    // Walk only known envelope keys and select the record matching the route
    // slug. Never fall back to the first product: that would restore the
    // product that happened to be rendered by SSR.
    const visit = (value: unknown, depth = 0): Record<string, unknown> | null => {
      if (depth > 8 || value === null || value === undefined) return null;
      if (Array.isArray(value)) {
        for (const entry of value) {
          const found = visit(entry, depth + 1);
          if (found) return found;
        }
        return null;
      }
      if (!this.isRecord(value)) return null;

      const record = value;
      if (record['slug'] || record['listingSlug'] || record['productSlug']) {
        const accepted = accept(record);
        if (accepted) return accepted;
      }

      for (const key of [
        'data', 'apiData', 'listingData', 'directListingDetails',
        'listing', 'item', 'response', 'result'
      ]) {
        const found = visit(record[key], depth + 1);
        if (found) return found;
      }
      return null;
    };

    const nested = visit(response);
    if (nested) return nested;

    return null;
  }

  private normalizeListing(item: Record<string, unknown>): FortuneListing {
    const images = Array.isArray(item['images']) ? item['images'] as Record<string, unknown>[] : [];
    const image = images[0];
    const prices = this.isRecord(item['prices']) ? item['prices'] : {};
    const creditMode = this.site.config().pricingMode === 'credit'
      || this.site.config().paymentMode === 'credit'
      || String(item['pricingMode'] ?? '').toLowerCase() === 'credit'
      || String(item['currency'] ?? item['currencyCode'] ?? '').toUpperCase() === 'CREDIT';

    const campaign = this.normalizeCampaign(item['campaign']);
    const seller = this.isRecord(item['seller']) ? item['seller'] : {};
    const status = this.resolveStatus(item, seller);
    const rawSellerImage = item['sellerHasImage'] === false ? '' : this.mediaPath(
      item['sellerMedia']
      ?? item['sellerImage']
      ?? item['sellerProfilePicture']
      ?? seller['profilePicture']
      ?? seller['imageUrl']
    );
    // Credit list responses are not perfectly uniform: older endpoints put
    // the value in `prices.price`, while the summary query exposes it as
    // `minPrice` and some compatibility envelopes use `creditAmount`.  In
    // credit mode all of these are credit values; never fall back to a fiat
    // currency conversion or leave the card with only the "Kredi" suffix.
    const rawBasePrice = this.toNumber(creditMode
      ? (prices['credit']
        ?? prices['creditAmount']
        ?? item['creditAmount']
        ?? item['creditPrice']
        ?? item['listingCredit'])
      : (item['minPrice'] ?? prices['price']));
    const basePrice = rawBasePrice !== null && rawBasePrice > 0
      ? rawBasePrice
      : this.detailBasePrice(item, creditMode);
    const campaignPrice = campaign && basePrice !== null && campaign.discountValue !== null
      ? campaign.discountType === 'percent'
        ? Math.max(0, (basePrice ?? 0) * (1 - campaign.discountValue / 100))
        : Math.max(0, (basePrice ?? 0) - campaign.discountValue)
      : null;
    const categorySlug = String(item['categorySlug'] ?? this.readRecordValue(item['category'], 'slug') ?? '').trim() || null;
    const rawCategoryTitle = String(item['categoryTitle'] ?? this.readRecordValue(item['category'], 'title') ?? '').trim();
    const categoryTitle = rawCategoryTitle && rawCategoryTitle.toLocaleLowerCase() !== 'fal'
      ? rawCategoryTitle
      : (categorySlug ? categorySlug.split('-').filter(Boolean).map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ') : '');

    return {
      id: Number(item['id'] ?? 0),
      versionID: item['versionID'] !== undefined && item['versionID'] !== null ? Number(item['versionID']) : null,
      sellerID: Number(item['sellerID'] ?? 0),
      sellerSlug: String(item['sellerSlug'] ?? item['sellerHandle'] ?? item['userName'] ?? item['shopName'] ?? '') || null,
      sellerShopName: String(item['shopName'] ?? item['sellerShopName'] ?? ''),
      title: String(item['title'] ?? ''),
      slug: String(item['slug'] ?? ''),
      description: (item['description'] ?? item['listingDescription'] ?? this.readRecordValue(item['listingLang'], 'description')) !== undefined && (item['description'] ?? item['listingDescription'] ?? this.readRecordValue(item['listingLang'], 'description')) !== null
        ? String(item['description'] ?? item['listingDescription'] ?? this.readRecordValue(item['listingLang'], 'description'))
        : null,
      categoryID: item['categoryID'] !== undefined && item['categoryID'] !== null ? Number(item['categoryID']) : null,
      categorySlug,
      categoryTitle,
      imageUrl: this.absoluteMediaUrl(this.mediaPath(image?.['url'])),
      imageSizes: this.parseSizes(image?.['sizes']),
      images: images.map((entry) => ({
        url: this.absoluteMediaUrl(this.mediaPath(entry['url'])),
        sizes: this.parseSizes(entry['sizes'])
      })),
      price: basePrice,
      currency: creditMode ? 'CREDIT' : String(item['currency'] ?? item['currencyCode'] ?? this.displayCurrency()).trim().toUpperCase(),
      sellerName: String(item['sellerName'] ?? seller['sellerName'] ?? seller['name'] ?? item['shopName'] ?? ''),
      sellerImage: this.absoluteMediaUrl(rawSellerImage),
      sellerImageSizes: this.parseSizes(item['sellerMediaSizes'] ?? item['sellerImageSizes'] ?? seller['profilePictureSizes']),
      rating: this.toNumber(item['rating']) ?? 0,
      reviewCount: Number(item['review'] ?? item['reviewCount'] ?? 0),
      orderCount: Number(item['sellerOrderCount'] ?? 0),
      listingType: String(item['listingType'] ?? item['productType'] ?? '') || null,
      freeShipping: this.isFreeShipping(item),
      liveStatus: (item['sellerPresenceStatus'] ?? item['sellerLiveStatus'] ?? item['sellerStatus'] ?? item['sellerLiveState'] ?? item['liveStatus'] ?? seller['liveStatus'] ?? seller['status'])
        ? String(item['sellerPresenceStatus'] ?? item['sellerLiveStatus'] ?? item['sellerStatus'] ?? item['sellerLiveState'] ?? item['liveStatus'] ?? seller['liveStatus'] ?? seller['status'])
        : null,
      presenceStatus: item['sellerPresenceStatus'] ? String(item['sellerPresenceStatus']) : null,
      presenceLastSeenAt: item['sellerPresenceLastSeenAt'] ? String(item['sellerPresenceLastSeenAt']) : null,
      pendingFortunes: Number(item['pendingFortunes'] ?? seller['pendingFortunes'] ?? 0),
      sellerScore: this.toNumber(item['sellerScore'] ?? seller['sellerScore']),
      listingScore: this.toNumber(item['listingScore'] ?? item['score']),
      statusLabel: status.label,
      statusCode: status.code,
      rankTitle: String(item['sellerRankTitle'] ?? item['rankTitle'] ?? '').trim() || null,
      rankReminderName: String(item['sellerRankReminderName'] ?? item['rankReminderName'] ?? '').trim() || null,
      isNewReader: status.isNew,
      online: this.isOnlineValue(
        item['sellerOnline'] ?? item['isSellerOnline'] ?? item['online'] ?? item['isOnline'] ?? item['sellerAvailable'] ?? item['available'] ?? item['sellerPresenceStatus'] ?? seller['online'] ?? seller['isOnline']
      ),
      highlightText: item['highlightText']
        ? String(item['highlightText'])
        : String(item['sellerRankTitle'] ?? item['sellerRankReminderName'] ?? seller['rankTitle'] ?? '').trim() || null,
      campaign,
      campaignPrice
    };
  }

  private normalizeCampaign(value: unknown): FortuneListingCampaign | null {
    if (!this.isRecord(value) || !Number(value['id'] ?? 0)) return null;
    return {
      id: Number(value['id']),
      title: String(value['title'] ?? ''),
      campaignType: String(value['campaignType'] ?? ''),
      discountType: value['discountType'] === null || value['discountType'] === undefined ? null : String(value['discountType']),
      discountValue: this.toNumber(value['discountValue']),
      buyQuantity: value['buyQuantity'] === null || value['buyQuantity'] === undefined ? null : Number(value['buyQuantity']),
      payQuantity: value['payQuantity'] === null || value['payQuantity'] === undefined ? null : Number(value['payQuantity']),
      sellerID: value['sellerID'] === null || value['sellerID'] === undefined ? null : Number(value['sellerID'])
    };
  }

  private isOnlineValue(value: unknown): boolean {
    if (value === true || value === 1) return true;
    const normalized = String(value ?? '').trim().toLowerCase();
    return ['1', 'true', 'online', 'available', 'active', 'live', 'on_duty', 'intraday', 'cevrimici', 'çevrimiçi', 'musait', 'müsait'].includes(normalized);
  }

  private resolveStatus(item: Record<string, unknown>, seller: Record<string, unknown>): { label: string | null; code: string | null; isNew: boolean } {
    const labelValue = item['statusLabel'] ?? item['fortuneStatus'] ?? item['readerStatus'] ?? item['displayStatus']
      ?? item['sellerStatusLabel'] ?? item['sellerRankTitle'] ?? item['rankTitle']
      ?? seller['statusLabel'] ?? seller['fortuneStatus'] ?? seller['readerStatus'] ?? seller['displayStatus']
      ?? seller['sellerStatusLabel'] ?? seller['sellerRankTitle'] ?? seller['rankTitle']
      ?? item['sellerStatus'] ?? seller['sellerStatus'];
    const codeValue = item['statusCode'] ?? item['fortuneStatusCode'] ?? item['readerStatusCode']
      ?? item['sellerStatusCode'] ?? item['sellerStatusID'] ?? item['sellerRankID']
      ?? seller['statusCode'] ?? seller['fortuneStatusCode'] ?? seller['readerStatusCode']
      ?? seller['sellerStatusCode'] ?? seller['sellerStatusID'] ?? seller['sellerRankID'];
    const liveValue = item['sellerLiveStatus'] ?? item['sellerStatus'] ?? item['sellerLiveState'] ?? item['liveStatus']
      ?? seller['liveStatus'] ?? seller['status'];
    const labelText = String(labelValue ?? liveValue ?? '').trim();
    const labelState = labelText.toLowerCase();
    const label = ['active', 'open', 'approved', 'pending', 'passive', 'inactive', 'closed', 'aktif', 'pasif', 'acik', 'açık', 'kapali', 'kapalı'].includes(labelState)
      ? null
      : (labelText || null);
    const code = String(codeValue ?? '').trim() || null;
    const rawNew = item['isNewReader'] ?? item['isNewSeller'] ?? item['isNew']
      ?? item['sellerRankReminderName'] ?? seller['isNewReader'] ?? seller['isNewSeller'] ?? seller['isNew']
      ?? seller['sellerRankReminderName'];
    const normalized = `${label ?? ''} ${code ?? ''}`.toLowerCase();
    return {
      label,
      code,
      isNew: rawNew === true || rawNew === 1 || ['new', 'yeni', 'new reader', 'new seller', 'yeni falcı', 'yeni falci'].some((value) => normalized.includes(value))
    };
  }

  private isFreeShipping(item: Record<string, unknown>): boolean {
    const direct = item['freeShipping'] ?? item['isFreeShipping'];
    if (direct === true || direct === 1 || String(direct ?? '').toLowerCase() === 'true') return true;
    const shipping = item['shippingDetails'] ?? item['shippingOptions'] ?? item['shippingProfile'];
    const routes = Array.isArray(shipping) ? shipping : [shipping];
    return routes.some((route) => {
      if (!this.isRecord(route)) return false;
      const price = this.toNumber(route['price'] ?? route['shippingPrice'] ?? route['amount']);
      const type = String(route['type'] ?? route['priceType'] ?? '').toLowerCase();
      return route['free'] === true || route['isFree'] === true || type === 'free' || price === 0;
    });
  }

  private normalizeFeatureFacets(value: unknown): FortuneFeatureFacet[] {
    if (this.isRecord(value)) value = value['features'] ?? value['featureFacets'] ?? value['categoryFeatures'] ?? value['items'] ?? value['data'] ?? [];
    if (!Array.isArray(value)) return [];
    const facets = new Map<number, FortuneFeatureFacet>();
    const counts = new Map<string, Set<string>>();
    for (const raw of value as unknown[]) {
      if (!this.isRecord(raw)) continue;
      const featureID = Number(raw['featureID'] ?? raw['id'] ?? 0);
      const valueID = Number(raw['featureDetailsID'] ?? raw['subFeatureID'] ?? raw['valueID'] ?? 0);
      if (!featureID || !valueID) continue;
      const facet = facets.get(featureID) ?? {
        id: featureID,
        key: this.slugKey(raw['featureKey'] ?? raw['featureSlug'] ?? raw['key'] ?? raw['slug'] ?? raw['featureName'] ?? raw['name'] ?? `feature-${featureID}`),
        title: String(raw['featureName'] ?? raw['name'] ?? raw['featureType'] ?? raw['slug'] ?? `Özellik ${featureID}`),
        values: []
      };
      const title = String(raw['subFeatureName'] ?? raw['featureDetailName'] ?? raw['value'] ?? raw['title'] ?? raw['subFeatureSlug'] ?? `Seçenek ${valueID}`);
      const key = `${featureID}:${valueID}`;
      const set = counts.get(key) ?? new Set<string>();
      set.add(String(raw['versionID'] ?? raw['listingID'] ?? raw['id'] ?? `${key}:${set.size}`));
      counts.set(key, set);
      if (!facet.values.some((item) => item.id === valueID)) facet.values.push({ id: valueID, title, count: 0 });
      facets.set(featureID, facet);
    }
    for (const facet of facets.values()) {
      facet.values = facet.values.map((item) => ({ ...item, count: counts.get(`${facet.id}:${item.id}`)?.size ?? 0 }));
    }
    return [...facets.values()].filter((facet) => facet.values.length);
  }

  private localePayload(): { lang: string; country: string; locale: string; currency: string } {
    return {
      lang: this.i18n.lang(),
      country: this.localePreference.country() || this.i18n.country(),
      locale: this.i18n.locale(),
      currency: this.displayCurrency()
    };
  }

  private displayCurrency(): string {
    const selectedCurrency = this.localePreference.currency().trim().toUpperCase();
    if (selectedCurrency) return selectedCurrency;
    const userCurrency = String(this.auth.user()?.locale?.currency ?? '').trim().toUpperCase();
    if (userCurrency) {
      return userCurrency;
    }
    const siteCurrency = String(this.site.config().currency ?? '').trim().toUpperCase();
    if (siteCurrency) return siteCurrency;
    const countryCurrencies: Record<string, string> = { TR: 'TRY', US: 'USD', GB: 'GBP', DE: 'EUR', ES: 'EUR', FR: 'EUR' };
    return countryCurrencies[this.i18n.country().toUpperCase()] ?? 'TRY';
  }

  private normalizeSeller(item: Record<string, unknown>): FortuneSeller {
    const status = this.resolveStatus(item, item);
    return {
      id: Number(item['id'] ?? 0),
      sellerName: String(item['sellerName'] ?? ''),
      shopName: String(item['shopName'] ?? ''),
      profilePicture: this.absoluteMediaUrl(this.mediaPath(item['profilePicture'] ?? item['imageUrl'])),
      profilePictureSizes: this.parseSizes(item['profilePictureSizes']),
      rating: this.toNumber(item['rating']) ?? 0,
      reviewCount: Number(item['reviewCount'] ?? 0),
      orderCount: Number(item['orderCount'] ?? 0),
      liveStatus: (item['presenceStatus'] ?? item['liveStatus'] ?? item['sellerStatus'] ?? item['status'])
        ? String(item['presenceStatus'] ?? item['liveStatus'] ?? item['sellerStatus'] ?? item['status'])
        : null,
      presenceStatus: item['presenceStatus'] ? String(item['presenceStatus']) : null,
      presenceLastSeenAt: item['presenceLastSeenAt'] ? String(item['presenceLastSeenAt']) : null,
      pendingFortunes: Number(item['pendingFortunes'] ?? 0),
      sellerScore: this.toNumber(item['sellerScore']),
      listingCount: Number(item['listingCount'] ?? 0),
      minCreditPrice: this.toNumber(item['minCreditPrice']),
      offerCategoryIDs: Array.isArray(item['offerCategoryIDs'])
        ? item['offerCategoryIDs'].map((value) => Number(value)).filter((value) => value > 0)
        : String(item['offerCategoryIDs'] ?? '').split(',').map((value) => Number(value)).filter((value) => value > 0),
      followerCount: Number(item['followerCount'] ?? 0),
      presentation: (item['presentation'] as ReaderPresentation|null) ?? null,
      statusLabel: status.label,
      statusCode: status.code,
      isNewReader: status.isNew,
      rankTitle: String(item['rankTitle'] ?? item['sellerRankTitle'] ?? item['sellerRankReminderName'] ?? '').trim() || null,
      rankReminderName: String(item['rankReminderName'] ?? item['sellerRankReminderName'] ?? '').trim() || null,
      highlightText: String(item['highlightText'] ?? item['sellerRankTitle'] ?? item['sellerRankReminderName'] ?? '').trim() || null
      ,online: this.isOnlineValue(item['online'] ?? item['isOnline'] ?? item['sellerOnline'] ?? item['available'] ?? item['presenceStatus'])
    };
  }

  private normalizeListingDetail(item: Record<string, unknown>): FortuneListingDetail {
    const seller = this.isRecord(item['seller']) ? item['seller'] : {};
    const images = Array.isArray(item['images']) ? item['images'] as Record<string, unknown>[] : [];
    const creditMode = this.site.config().pricingMode === 'credit' || this.site.config().paymentMode === 'credit';
    const normalizedBase = this.normalizeListing({
      ...item,
      sellerID: item['sellerID'] ?? seller['sellerID'] ?? seller['id'] ?? 0,
      sellerSlug: seller['userName'] ?? seller['shopName'] ?? item['sellerSlug'] ?? item['sellerHandle'],
      shopName: seller['shopName'] ?? item['shopName'],
      sellerName: seller['name'] ?? seller['shopName'],
      sellerMedia: seller['profilePicture'],
      sellerMediaSizes: seller['profilePictureSizes'],
      categoryID: item['categoryID'] ?? this.readRecordValue(item['category'], 'id'),
      categorySlug: item['categorySlug'] ?? this.readRecordValue(item['category'], 'slug'),
      currency: creditMode ? 'CREDIT' : (item['currency'] ?? item['currencyCode'] ?? this.displayCurrency()),
      seoPrice: this.toNumber(item['seoPrice']),
      seoCurrency: String(item['seoCurrency'] ?? '').trim().toUpperCase() || null,
      creditUnitValue: this.toNumber(item['creditUnitValue']) ?? undefined,
      prices: {
        price: creditMode
          ? (this.toNumber(item['creditAmount']
            ?? item['creditPrice']
            ?? item['listingCredit']
            ?? this.detailBasePrice(item, true)))
          : (item['price'] ?? this.detailBasePrice(item))
      }
    });
    const listingReviews = Array.isArray(item['reviews']) ? item['reviews'] as Array<Record<string, unknown>> : [];
    const ratedListingReviews = listingReviews.map((review) => this.toNumber(review['rating'] ?? review['score'])).filter((rating): rating is number => rating !== null && rating > 0);
    const listingReviewCount = Math.max(Number(normalizedBase.reviewCount || 0), listingReviews.length);
    const listingRating = normalizedBase.rating > 0
      ? normalizedBase.rating
      : (ratedListingReviews.length ? ratedListingReviews.reduce((sum, rating) => sum + rating, 0) / ratedListingReviews.length : 0);

    const rawType = item['listingType'] ?? item['productType'] ?? item['type'] ?? item['listingTypeName'] ?? '';
    return {
      ...normalizedBase,
      rating: listingRating,
      reviewCount: listingReviewCount,
      categoryPath: this.normalizeCategoryPath(item, normalizedBase.categoryTitle, normalizedBase.categorySlug),
      listingType: String(rawType),
      preparationDays: this.toNumber(item['deliveryTime'] ?? item['preparationDays'] ?? item['processingDays'] ?? (this.isRecord(item['parametersData']) ? item['parametersData']['deliveryTime'] : null)),
      durationMinutes: this.toNumber(item['durationMinutes'] ?? item['duration']) ?? undefined,
      description: String(item['description'] ?? ''),
      images: images.map((image) => ({
        url: this.absoluteMediaUrl(String(image['url'] ?? '')),
        sizes: this.parseSizes(image['sizes'])
      })).filter((image) => !!image.url),
      options: (Array.isArray(item['options']) ? item['options'] as Record<string, unknown>[] : []).map((option) => ({
        id: Number(option['id'] ?? 0),
        name: String(option['title'] ?? option['name'] ?? option['translation'] ?? option['key'] ?? ''),
        processType: String(option['processType'] ?? ''),
        required: !!option['required'],
        subOptions: (Array.isArray(option['subOptions']) ? option['subOptions'] as Record<string, unknown>[] : []).map((sub) => ({
          id: Number(sub['id'] ?? 0),
          title: String(sub['customTitle'] ?? sub['title'] ?? sub['translation'] ?? sub['name'] ?? sub['value'] ?? ''),
          active: sub['active'] === undefined ? true : Boolean(sub['active']),
          info: sub['subOptionInfo'] ? String(sub['subOptionInfo']) : null,
          price: this.toNumber(creditMode ? (sub['credit'] ?? sub['creditPrice']) : sub['price'])
        }))
      })),
      customization: (Array.isArray(item['customization']) ? item['customization'] as Record<string, unknown>[] : []).map((field) => ({
        id: Number(field['id'] ?? 0),
        formKey: String(field['formKey'] ?? field['key'] ?? '').trim() || `field_${field['id'] ?? 0}`,
        type: String(field['type'] ?? 'text').toLowerCase(),
        title: String(field['title'] ?? field['label'] ?? field['formKey'] ?? field['key'] ?? ''),
        description: field['description'] ? String(field['description']) : null,
        required: !!field['required'],
        minLength: this.toNumber(field['minLength'] ?? field['minlength'] ?? field['minlenght']),
        maxLength: this.toNumber(field['maxLength'] ?? field['maxlength'] ?? field['maxlenght']),
        minFiles: this.toNumber(field['minFiles'] ?? field['minFileCount'] ?? field['minImages'] ?? field['minUploads'] ?? field['minLength'] ?? field['minlength'] ?? field['minlenght']),
        maxFiles: this.toNumber(field['maxFiles'] ?? field['maxFileCount'] ?? field['maxImages'] ?? field['maxUploads']),
        options: (Array.isArray(field['values']) ? field['values'] as Record<string, string>[] : (Array.isArray(field['options']) ? field['options'] as Record<string, string>[] : [])).map((option) => ({
          title: String(option['label'] ?? option['translate'] ?? option['title'] ?? option['name'] ?? option['value'] ?? ''),
          value: String(option['value'] ?? option['id'] ?? option['code'] ?? option['key'] ?? option['title'] ?? '')
        }))
      })),
      laws: this.isRecord(item['laws']) ? item['laws'] : {},
      policies: this.isRecord(item['policies']) ? item['policies'] : {},
      shipping: item['shipping'] ?? item['shippingDetails'] ?? item['shippingOptions'] ?? null,
      shippingUnavailable: item['shippingUnavailable'] === true || item['shippingUnavailable'] === 1,
      faqs: item['faqs'] ?? item['faq'] ?? item['faqItems'] ?? [],
      reviews: listingReviews,
      sellerReviews: Array.isArray(item['sellerReviews'])
        ? item['sellerReviews'] as Array<Record<string, unknown>>
        : (Array.isArray(seller['reviews']) ? seller['reviews'] as Array<Record<string, unknown>> : []),
      shippingDetails: this.isRecord(item['shippingDetails']) ? item['shippingDetails'] : null
      ,paymentGateways: (Array.isArray(item['paymentGateways']) ? item['paymentGateways'] as Record<string, unknown>[] : []).map((gateway) => ({
        id: Number(gateway['id'] ?? gateway['paymentGatewayID'] ?? 0),
        method: String(gateway['method'] ?? gateway['code'] ?? '').trim().toLowerCase(),
        fastPayment: gateway['fastPayment'] === true || gateway['fastPayment'] === 1 || gateway['fastPayment'] === '1',
        listingTypeID: gateway['listingTypeID'] == null ? null : Number(gateway['listingTypeID'])
      })).filter((gateway) => gateway.id > 0 && gateway.method)
    };
  }

  private applySeededSellerSnapshot(detail: FortuneListingDetail): void {
    const response=this.pageData.get<unknown>('listingSeller');
    if(!this.isRecord(response))return;
    const data=this.isRecord(response['data'])?response['data']:response;
    const seller=this.isRecord(data['seller'])?data['seller']:null;
    if(!seller||Number(seller['id']??seller['sellerID']??0)!==detail.sellerID)return;
    detail.sellerName=String(seller['sellerName']??detail.sellerName);
    detail.sellerShopName=String(seller['shopName']??detail.sellerShopName??'');
    detail.sellerSlug=String(seller['userName']??seller['shopName']??detail.sellerSlug??'')||detail.sellerSlug;
    detail.sellerImage=this.absoluteMediaUrl(this.mediaPath(seller['profilePicture']))||detail.sellerImage;
    detail.sellerImageSizes=this.parseSizes(seller['profilePictureSizes'])||detail.sellerImageSizes;
    detail.orderCount=Number(seller['orderCount']??detail.orderCount);
    detail.liveStatus=String(seller['liveStatus']??seller['presenceStatus']??detail.liveStatus??'')||null;
    detail.presenceStatus=String(seller['presenceStatus']??'')||null;
    detail.presenceLastSeenAt=String(seller['presenceLastSeenAt']??'')||null;
    detail.pendingFortunes=Number(seller['pendingFortunes']??0);
    detail.online=this.isOnlineValue(seller['online']??seller['presenceStatus']);
    detail.rankTitle=String(seller['rankTitle']??seller['rankReminderName']??'')||null;
    detail.highlightText=String(seller['highlightText']??'')||detail.highlightText;
  }

  private async refreshListingSellerSnapshot(detail: FortuneListingDetail): Promise<void> {
    try {
      const result = await this.sellerList({ search: detail.sellerName, limit: 10, page: 1 });
      const seller = result.items.find((entry) => entry.id === detail.sellerID);
      if (!seller) return;
      detail.liveStatus = seller.liveStatus;
      detail.presenceStatus = seller.presenceStatus ?? null;
      detail.presenceLastSeenAt = seller.presenceLastSeenAt ?? null;
      detail.online = !!seller.online;
      detail.pendingFortunes = seller.pendingFortunes ?? detail.pendingFortunes;
      detail.orderCount = seller.orderCount || detail.orderCount;
      detail.rankTitle = seller.rankTitle || seller.rankReminderName || detail.rankTitle;
      detail.highlightText = seller.highlightText || detail.highlightText;
    } catch {
      // Product detail remains usable when the live seller snapshot is temporarily unavailable.
    }
  }

  private normalizeCategoryPath(item: Record<string, unknown>, title: string, slug?: string | null): Array<{ title: string; slug?: string | null }> {
    const category = this.isRecord(item['category']) ? item['category'] : {};
    const raw = item['categoryPath'] ?? item['breadcrumbs'] ?? category['path'];
    const path = Array.isArray(raw) ? raw : typeof raw === 'string' ? raw.split(/\s*(?:->|>|\/)\s*/).filter(Boolean) : [];
    const normalized = path.map((entry) => {
      if (typeof entry === 'string') return { title: entry.trim(), slug: null };
      if (!this.isRecord(entry)) return null;
      return { title: String(entry['title'] ?? entry['name'] ?? '').trim(), slug: String(entry['slug'] ?? '').trim() || null };
    }).filter((entry): entry is { title: string; slug: string | null } => !!entry?.title);
    if (title && (!normalized.length || normalized.at(-1)?.title !== title)) {
      normalized.push({ title, slug: slug || null });
    }
    return normalized;
  }

  private mediaPath(value: unknown): string {
    if (this.isRecord(value)) value = value['url'] ?? value['path'] ?? value['filePath'] ?? value['mediaPath'] ?? value['original'];
    if (typeof value !== 'string') return '';
    const path = value.trim();
    const normalized = path.toLowerCase();
    return !path
      || ['null', 'undefined', '0', 'false', '[object object]'].includes(normalized)
      || normalized.startsWith('https:///')
      || normalized.startsWith('http:///')
      ? ''
      : path;
  }

  private slugKey(value: unknown): string {
    return String(value ?? '').trim().toLocaleLowerCase('en-US')
      .replace(/[ıİ]/g, 'i').replace(/[şŞ]/g, 's').replace(/[ğĞ]/g, 'g')
      .replace(/[üÜ]/g, 'u').replace(/[öÖ]/g, 'o').replace(/[çÇ]/g, 'c')
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  private absoluteMediaUrl(url: string | null): string | null {
    if (!url || url === 'null' || url === 'undefined') {
      return null;
    }
    if (/^https?:\/\/\//.test(url)) {
      return null;
    }
    if (/^https?:\/\//.test(url)) {
      return url;
    }

    return `${this.site.config().apiBaseUrl}/${url.replace(/^\/+/, '')}`;
  }

  private detailBasePrice(item: Record<string, unknown>, creditMode = false): number | null {
    const parametersData = this.isRecord(item['parametersData']) ? item['parametersData'] : {};
    const responsePrices = this.isRecord(item['prices']) ? item['prices'] : {};
    const direct = creditMode
      ? (item['creditAmount'] ?? item['creditPrice'] ?? item['listingCredit'])
      : item['price'];
    const directValue = this.toNumber(direct);
    if (directValue !== null && directValue > 0) return directValue;
    const parameterKey = creditMode ? 'credit' : 'price';
    const prices = Array.isArray(parametersData[parameterKey])
      ? parametersData[parameterKey] as Record<string, unknown>[]
      : (!creditMode && Array.isArray(parametersData['price'])
        ? parametersData['price'] as Record<string, unknown>[]
        : []);
    const base = prices.find((price) => !price['subOptionID'] && !price['combinationID']);
    if (base) {
      const baseValue = this.toNumber(base['value']);
      if (baseValue !== null && baseValue > 0) return baseValue;
    }
    const values = prices
      .filter((price) => price['value'] !== undefined && price['value'] !== null)
      .map((price) => this.toNumber(price['value']))
      .filter((value): value is number => value !== null && value > 0);
    if (!values.length) {
      if (creditMode) {
        const subOptions = responsePrices['subOptions'];
        const variationValues = (Array.isArray(subOptions) ? subOptions : Object.values(
          this.isRecord(subOptions) ? subOptions : {}
        ))
          .filter((sub): sub is Record<string, unknown> => this.isRecord(sub))
          .map((sub) => this.toNumber(sub['credit'] ?? sub['creditPrice'] ?? sub['price']))
          .filter((value): value is number => value !== null && value > 0);
        if (variationValues.length) return Math.min(...variationValues);
      }
      const options = Array.isArray(item['options']) ? item['options'] as Record<string, unknown>[] : [];
      const variationValues = options
        .filter((option) => String(option['processType'] ?? '').toLowerCase() === 'variation')
        .flatMap((option) => Array.isArray(option['subOptions']) ? option['subOptions'] as Record<string, unknown>[] : [])
        .map((sub) => this.toNumber(creditMode ? (sub['credit'] ?? sub['creditPrice'] ?? sub['price']) : sub['price']))
        .filter((value): value is number => value !== null && value > 0);
      return variationValues.length ? Math.min(...variationValues) : null;
    }
    return values.length ? Math.min(...values) : null;
  }

  private readRecordValue(item: unknown, key: string): unknown {
    return this.isRecord(item) ? item[key] : null;
  }

  private parseSizes(value: unknown): MediaSizes {
    if (Array.isArray(value) || this.isRecord(value) || value == null) {
      return value;
    }
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) || this.isRecord(parsed) ? parsed : null;
      } catch {
        return value ? [value] : null;
      }
    }

    return null;
  }

  private isRecord(value: unknown): value is Record<string, any> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  private toNumber(value: unknown): number | null {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
}
